import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does BCP\'s GREEN condition scorecard mean I cannot claim?',
    answer: 'No. The Department for Transport rates BCP\'s condition GREEN, but the overall rating is still AMBER and Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. BCP filled 3,300 potholes in 2024/25 — up 34% from 2021/22 — A-road RED condition rose from 2.5% to 3.4% between 2022 and 2024, and B/C roads from 3.1% to 4.2%. Aggregate condition scores do not prove your pothole was caught in time.',
  },
  {
    question: 'BCP reports 75–83% preventative maintenance — doesn\'t that mean they are trying?',
    answer: 'High preventative percentages do not automatically prevent potholes. BCP spent an estimated 83% on preventative work in 2023/24, yet still filled 3,290 potholes that year — 34% more than in 2021/22. The council itself states that over the last five years an estimated 75% of reactive revenue maintenance spend went on safety-defect pothole repairs alone. Results on A and B/C roads show RED condition rising despite the stated "prevention is better than cure" ethos.',
  },
  {
    question: 'Why did pothole repairs rise 34% between 2021/22 and 2024/25?',
    answer: 'BCP\'s transparency report records 2,463 pothole repairs in 2021/22 and 3,300 in 2024/25. Over the same period, A-road RED condition rose from 2.5% to 3.4% and B/C roads from 3.1% to 4.2%. The council admits some roads were not built for current traffic levels and that construction has not always been upgraded — accelerating deterioration on routes that now carry heavier traffic.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified (U) roads make up 935.0km — 75% of BCP\'s 1,248.4km carriageway network. The council surveys 25% of U-roads each year on a rotating four-area cycle, not the full residential network annually. Published U-road RED condition has held at 0.9% since 2022, but a 12-month AI trial covering 100% of U-roads is underway — and the council warns comparison with SCANNER data is likely to show an increase in RED condition.',
  },
  {
    question: 'What does it mean that 75% of BCP\'s reactive revenue goes to pothole repairs?',
    answer: 'It means three-quarters of reactive revenue maintenance spend over the last five years has been fixing potholes — not drainage cleansing, vegetation, signs or other reactive work. That contradicts the council\'s stated aim to "use predominantly preventative maintenance to keep the reactive maintenance to a minimum." It is evidence of a network still producing safety-critical defects at scale, not proof your specific defect was unavoidable.',
  },
  {
    question: 'BCP is trialling AI surveys on U-roads — does that help or hurt my claim?',
    answer: 'Potentially both, depending on timing. BCP states the AI trial and SCANNER comparison "is likely to result in an increase in red condition due to the differences in surveys and condition ratings." That suggests current 0.9% RED on U-roads may understate defects detected under the new method — which can support arguments that inspection records lag reality. For your claim, prior reports, photos showing defect age and the road\'s place in the four-year U-road survey cycle matter more than future survey technology.',
  },
  {
    question: 'How do I report a pothole to BCP Council?',
    answer: 'Report potholes via BCP\'s online form at bcpcouncil.gov.uk — you cannot use the reporting form to make an insurance claim. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BournemouthChristchurchPooleCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="BCP Pothole Claims | 3,300 Repairs on an Amber-Rated Network | Fixtyer"
        description="BCP Council earns GREEN DfT condition ratings but filled 3,300 potholes in 2024/25, surveys just 25% of U-roads per year, and admits AI trials may reveal more defects. What that means for your Section 58 claim."
        canonical="/councils/bournemouth-christchurch-and-poole"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Bournemouth, Christchurch and Poole', url: '/councils/bournemouth-christchurch-and-poole' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Bournemouth, Christchurch and Poole Council"
        councilSlug="bournemouth-christchurch-and-poole"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/bournemouth-christchurch-and-poole"
        headline="Bournemouth, Christchurch and Poole Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Bournemouth, Christchurch and Poole</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            3,300 Pothole Repairs in 2024/25 on an Amber-Rated Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Bournemouth, Christchurch and Poole Council maintains <strong className="text-white">1,248.4km</strong> of
            roads — <strong className="text-rose-400">935km of unclassified residential network (75%)</strong>.
            The DfT rates the council <strong className="text-amber-400">AMBER</strong> overall despite a{' '}
            <strong className="text-emerald-400">GREEN</strong> condition scorecard. Its own report records{' '}
            <strong className="text-rose-400">3,300 pothole repairs in 2024/25</strong>, up 34% from 2021/22, while
            A-road RED condition rose from 2.5% to 3.4% and B/C roads from 3.1% to 4.2%. Section 58 still turns on
            your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">75%</div>
            <div className="text-white font-medium mt-2">Of reactive revenue on pothole repairs</div>
            <div className="text-brand-300 text-sm mt-1">
              Over the last five years, BCP estimates three-quarters of reactive revenue maintenance spend went on
              safety-defect pothole repairs — while projecting 3,179 more fills in 2025/26 and treating just 10.7
              miles (1.4%) of the network preventatively this year.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,248.4km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from BCP's June 2025 transparency report — where most pothole claims start
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,248.4km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">935.0km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">75% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">177.4km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">136.0km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">A35 corridor and principal routes</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">BCP highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">1,760.1km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Other public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">157.3km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">273.0km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges and structures</td>
                  <td className="px-6 py-4 text-center font-medium">281</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Drainage gullies</td>
                  <td className="px-6 py-4 text-center font-medium">59,706</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">38,196*</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-xs mb-6">
            *Street lighting figures cover Bournemouth and Poole only — Christchurch street lighting remains under the
            Dorset PFI contract.
          </p>

          <QuoteCard
            quote="Some of the road network has evolved over time, meaning traffic may have increased on a road and the construction not changed, increasing the rate of deterioration."
            source="BCP Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What GREEN Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER survey data from BCP's transparency report — with U-roads measured on a four-year rotating cycle
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> BCP uses SCANNER laser surveys for condition data. U-roads are
              surveyed every year but only 25% of the network each year on a rotating four-area cycle — the same
              25% slice in 2020 is surveyed again in 2024. A 12-month AI trial now covers 100% of U-roads; the council
              states the AI and SCANNER comparison "is likely to result in an increase in red condition due to the
              differences in surveys and condition ratings." From 2026/27, PAS 2161 will replace the current
              three-category system nationally.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (136.0km) — surveyed annually</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.5%</td>
                    <td className="px-4 py-2 text-center">18.3%</td>
                    <td className="px-4 py-2 text-center">79.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.2%</td>
                    <td className="px-4 py-2 text-center">19.8%</td>
                    <td className="px-4 py-2 text-center">77.0%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.4%</td>
                    <td className="px-4 py-2 text-center font-bold">22.2%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">74.4%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition rose from 2.5% in 2022 to 3.4% in 2024 — a 36% relative increase. Principal routes
              such as the A35 still show majority green ratings, but deterioration on 136km of major roads feeds the
              DfT's mixed picture alongside AMBER spend and best-practice scorecards.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (177.4km) — surveyed annually</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.1%</td>
                    <td className="px-4 py-2 text-center">21.6%</td>
                    <td className="px-4 py-2 text-center">75.3%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3.9%</td>
                    <td className="px-4 py-2 text-center">24.1%</td>
                    <td className="px-4 py-2 text-center">72.0%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.2%</td>
                    <td className="px-4 py-2 text-center font-bold">25.5%</td>
                    <td className="px-4 py-2 text-center font-bold">70.3%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C RED condition rose from 3.1% to 4.2% between 2022 and 2024 — a 35% relative increase — while
              green-rated B/C roads fell from 75.3% to 70.3%.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (935.0km) — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">0.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">0.9%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">0.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              U-road RED has held at 0.9% since 2022 — roughly 8.4km of critical residential carriageway on published
              figures — but only 25% of U-roads are SCANNER-surveyed each year. The ongoing AI trial covering 100% of
              U-roads may reveal higher RED percentages when compared with historic SCANNER results.
            </p>
          </div>

          <QuoteCard
            quote="The AI trial and SCANNER comparison over the coming years is likely to result in an increase in red condition due to the differences in surveys and condition ratings."
            source="BCP Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">Following the Money</h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN condition but AMBER spend — high preventative percentages alongside rising pothole volumes
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">BCP highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">7,478</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">7,478</td>
                  <td className="px-4 py-3 text-center">2,138</td>
                  <td className="px-4 py-3 text-center text-emerald-400">78%</td>
                  <td className="px-4 py-3 text-center">22%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">5,390</td>
                  <td className="px-4 py-3 text-center">6,764</td>
                  <td className="px-4 py-3 text-center">1,855</td>
                  <td className="px-4 py-3 text-center">77%</td>
                  <td className="px-4 py-3 text-center">23%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">6,241</td>
                  <td className="px-4 py-3 text-center">8,227</td>
                  <td className="px-4 py-3 text-center">1,633</td>
                  <td className="px-4 py-3 text-center">83%</td>
                  <td className="px-4 py-3 text-center">17%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">4,786</td>
                  <td className="px-4 py-3 text-center">7,234</td>
                  <td className="px-4 py-3 text-center">2,388</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">4,786</td>
                  <td className="px-4 py-3 text-center">7,646</td>
                  <td className="px-4 py-3 text-center">1,502</td>
                  <td className="px-4 py-3 text-center">82%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">10,773*</td>
                  <td className="px-4 py-3 text-center">4,998</td>
                  <td className="px-4 py-3 text-center">1,804</td>
                  <td className="px-4 py-3 text-center">75%</td>
                  <td className="px-4 py-3 text-center">25%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-brand-400 text-xs mb-8">
            *2020/21 DfT allocation includes £4.184m Challenge Fund awarded for schemes delivered across five years.
            Spending figures verified from DfT Annex B after initial publication, per the council's report.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why condition is GREEN</h3>
              <p className="text-brand-200 text-sm">
                SCANNER data shows majority green ratings on A and B/C roads, U-road RED at 0.9%, and BCP reports
                76.5 miles (122.4km) of preventative surfacing over the last five years. The DfT condition scorecard
                reflects this network-level picture.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why overall is still AMBER</h3>
              <p className="text-brand-200 text-sm">
                Spend and best-practice scorecards are both AMBER. Despite 77–83% preventative shares in recent years,
                pothole repairs rose to 3,300 in 2024/25, A/B RED condition is trending up, 58 bridges (21%) are in
                poor or very poor condition, and U-road condition relies on partial annual surveys until the AI trial
                completes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Pothole Repair Counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated safety-defect pothole repairs from BCP's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">BCP estimated potholes filled 2020-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2021/22</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,806</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,463</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,593</td>
                  <td className="px-6 py-4 text-center text-amber-600">+5.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">3,290</td>
                  <td className="px-6 py-4 text-center text-amber-600">+33.6%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,300</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+34.0%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">3,179</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="Over the last 5 years, BCP have used an estimated 75% of the reactive revenue maintenance spend on safety defect pothole repairs."
              source="BCP Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Roughly nine patches a day</h3>
              <p className="text-surface-600 text-sm">
                3,300 pothole repairs in 2024/25 works out to about 9.0 patches per day across 1,248.4km. That volume
                of reactive safety work sits alongside 77% preventative spend — evidence that defects still form faster
                than surfacing programmes prevent them, especially on routes where traffic has outgrown original
                construction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How BCP says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> SCANNER surveys annually (136.0km)</li>
                <li>• <strong>B/C roads:</strong> SCANNER surveys annually (177.4km)</li>
                <li>• <strong>U roads:</strong> 25% of network surveyed each year on a four-area rotating cycle</li>
                <li>• <strong>AI trial:</strong> 12-month trial covering 100% of U-roads underway</li>
                <li>• <strong>Bridges:</strong> 281 structures — 58 (21%) in poor or very poor condition</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Safety inspections</h3>
              <p className="text-surface-600 text-sm mb-3">
                BCP inspects all reported potholes against investigatory criteria in its highway inspection policy.
                Urgent defects are repaired first; others are recorded for wider maintenance schemes. Reporting a
                pothole does not substitute for an insurance claim — but it creates a notice record.
              </p>
              <p className="text-surface-600 text-sm">
                Reactive safety-defect records are used alongside SCANNER data to prioritise maintenance programmes.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, BCP must maintain
              public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it must
              show a reasonable system for inspecting and repairing the specific defect — not just publish a green
              condition scorecard.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your U-road on the current year's 25% SCANNER slice — or between survey cycles?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, council portal) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="Our aim is to use predominantly preventative maintenance to keep the reactive maintenance to a minimum, whilst meeting our statutory duties."
            source="BCP Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What BCP says it will deliver this financial year
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">10.7 miles</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Preventative carriageway work</div>
              <div className="text-xs text-emerald-700 mt-1">3.3 miles resurfacing + 7.4 miles surface treatment</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">16</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Footway schemes</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">4</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Bridges and structures</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">3,179</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Potholes projected</div>
            </div>
          </div>

          <QuoteCard
            quote="The ethos for delivering the highway asset management service is prevention is better than cure. An annual preventative maintenance programme slows the rate of condition deterioration over time which is demonstrated by the condition data and aligns to this ethos and best practice."
            source="BCP Council Local Highways Maintenance Transparency Report (June 2025)"
          />

          <div className="mt-6 bg-surface-50 rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              BCP plans 10.7 miles (17.1km) of preventative carriageway work on a 775.6-mile network — roughly 1.4%
              in a single year. Published RED condition on A and B/C roads alone totals about 12.1km requiring
              attention. The council projects 3,179 pothole repairs while treating a fraction of the network
              preventatively — reactive workload remains the dominant safety mechanism on residential roads surveyed
              only in quarterly slices.
            </p>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What BCP Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the June 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On bridge condition</h3>
              <QuoteCard
                quote="Currently 58 (21%) Bridges & Structures are in poor or very poor condition. 18 of these 58 Bridges & Structures (31%) are part of the resilient network."
                source="BCP Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On reactive pothole dominance</h3>
              <QuoteCard
                quote="Over the last 5 years, BCP have used an estimated 75% of the reactive revenue maintenance spend on safety defect pothole repairs."
                source="BCP Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On evolving traffic loads</h3>
              <QuoteCard
                quote="Some of the road network has evolved over time, meaning traffic may have increased on a road and the construction not changed, increasing the rate of deterioration."
                source="BCP Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an Amber-Rated Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: BCP is not Waltham Forest — here is how the mixed scorecards change your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council's favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN DfT condition scorecard on published SCANNER data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 73–83% preventative maintenance share in recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 122.4km of preventative surfacing over the last five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Full SCANNER coverage on A and B/C roads annually
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management aligned to Well-Managed Highway Infrastructure guidance
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a structured Section 58 defence on principal A-roads where green ratings remain above 74%.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall, spend and best-practice scorecards from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 935km of U-roads — 75% of network — only 25% SCANNER-surveyed per year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 3,300 pothole repairs in 2024/25 — up 34% since 2021/22
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road RED up 36% and B/C RED up 35% relative (2022–2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council warns AI surveys will likely show higher U-road RED than SCANNER
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 75% of reactive revenue spent on pothole repairs over five years
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN condition scorecard and high preventative percentages, your claim lives
              or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the four-year partial survey cycle is your strongest structural argument</li>
              <li>• Location on routes where the council admits traffic has outgrown original construction</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from BCP's own
              transparency data where it helps you — without pretending the council is failing on every metric.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to BCP Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack. You cannot use the reporting form to make an
            insurance claim.
          </p>
          <a
            href="https://www.bcpcouncil.gov.uk/roads-and-travel/report-a-problem-with-a-road-or-street/report-a-pothole"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — bcpcouncil.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Bournemouth, Christchurch or Poole?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            Mixed DfT scorecards demand a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road partial survey-cycle argument</li>
                <li>• No prior-report search</li>
                <li>• No AMBER overall scorecard challenge</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 935km U-road survey-cycle gap documented</li>
                <li>✅ 3,300 pothole repairs in 2024/25 cited</li>
                <li>✅ A/B RED deterioration trend referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to BCP</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=bournemouth-christchurch-and-poole"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My BCP Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.bcpcouncil.gov.uk/roads-and-travel/how-we-maintain-our-roads/highway-maintenance-programme"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              BCP Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
